import { Form, Typography } from "antd";
import { useEffect } from "react";
import {
	collectPlaceholderValues,
	type PlaceholderField,
	type PlaceholderValue,
} from "@/common/placeholders";
import { Wizard } from "@/components/wizard";
import { PlaceholderFormItem } from "./placeholder-input";

interface FillPlaceholdersWizardProps {
	open: boolean;
	/** What the template is being used for, e.g. "Create task from template". */
	title: string;
	/** The template's name, shown above the inputs. */
	templateName: string;
	fields: PlaceholderField[];
	completeLabel?: string;
	/** Receives one value per placeholder key, ready for `substituteVariables`. */
	onSubmit: (values: Record<string, PlaceholderValue>) => void;
	onCancel: () => void;
}

/** Asks for a template's placeholder values before the template is used. */
export function FillPlaceholdersWizard({
	open,
	title,
	templateName,
	fields,
	completeLabel = "Continue",
	onSubmit,
	onCancel,
}: FillPlaceholdersWizardProps) {
	const [form] = Form.useForm();

	// Each open starts from the template's defaults, not the last values typed.
	useEffect(() => {
		if (open) form.resetFields();
	}, [open, form]);

	// The wizard re-validates in the background as values settle, which can supersede
	// this validation mid-flight (rc-field-form rejects it as outOfDate, with no errors):
	// retry until one completes cleanly.
	const submit = () =>
		form
			.validateFields()
			.then(() =>
				onSubmit(
					collectPlaceholderValues(fields, (key) => form.getFieldValue(key)),
				),
			)
			.catch((e: { outOfDate?: boolean }) => {
				if (e?.outOfDate) submit();
			});

	return (
		<Wizard
			open={open}
			title={title}
			completeLabel={completeLabel}
			form={form}
			onComplete={submit}
			onCancel={onCancel}
			pages={[
				{
					id: "placeholders",
					title: "Placeholders",
					content: (
						<>
							<Typography.Paragraph>
								<Typography.Text strong>{templateName}</Typography.Text> needs a
								few values. Each one replaces its placeholder in the template's
								text; you can still review everything in the next step.
							</Typography.Paragraph>
							{fields.map((field) => (
								<PlaceholderFormItem
									key={field.key}
									field={field}
									style={{ maxWidth: 480 }}
								/>
							))}
						</>
					),
				},
			]}
		/>
	);
}
